/** RFC 0345 3i: who can see a target, each with why, for a Share list.
 * Shared by a browser window and a Mac's daemon. */
import type { SharingInviteEntry } from '../model/world/types'
import type {
  TargetKeyBindings,
  TargetMembership,
  TargetPresence,
} from './membership'

export interface MemberRow {
  id: string
  kind: 'person' | 'agent' | 'group' | 'key'
  name: string
  reason: string
  present: boolean
  sessionId?: string
  personName?: string
}

/** Names for people who are not here now, each bound to its id by something
 * the viewer can check. Never a name read from a document others can write. */
export interface KnownIdentities {
  /** The viewer's own person: their own account says who they are. */
  self?: { person: string; name: string }
  /** The viewer's own invite records (their private World). */
  invites?: Readonly<Record<string, SharingInviteEntry>>
  /** From core `bindTargetKeys`: devices, agents and issuers, by signature. */
  keys?: TargetKeyBindings
  /** The target, when it is a session: what an agent's mark is drawn from. */
  session?: string
}

/** Person id to the email the viewer invited them as. Only `joined` ids count:
 * those are the people the invite key itself admitted, read off its signed
 * grants when the viewer's device settled it. A pending invite names no one. */
export function invitedPeople(
  invites: KnownIdentities['invites'],
): Map<string, string> {
  const named = new Map<string, string>()
  const entries = Object.values(invites ?? {}).sort(
    (a, b) => Number(a.createdAt) - Number(b.createdAt),
  )
  for (const invite of entries) {
    const status = String(invite.status)
    if (status !== 'joined' && status !== 'removed') continue
    for (const id of invite.joined ?? [])
      named.set(String(id), String(invite.email))
  }
  return named
}

/** One-time invite keys still pending, by key id, from the viewer's records. */
function pendingInviteKeys(invites: KnownIdentities['invites']) {
  const keys = new Map<string, string>()
  for (const invite of Object.values(invites ?? {}))
    if (String(invite.status) === 'pending')
      keys.set(String(invite.inviteKey), String(invite.email))
  return keys
}

const short = (id: string) => id.slice(0, 8)

/** Only pass verified presence and signed bindings. Names identify their own
 * signer, never grant access. Unidentified principals stay visible: a missing
 * profile cannot hide a grant. */
export function projectMembership(
  membership: TargetMembership,
  presence: readonly TargetPresence[],
  now: number,
  known: KnownIdentities = {},
): MemberRow[] {
  const principals = new Map(membership.principals.map((p) => [p.id, p]))
  const live = presence.filter(
    (p) =>
      now - p.at <= 30000 &&
      p.at <= now + 5000 &&
      principals.has(p.person) &&
      principals.has(p.signer),
  )
  const people = new Map(
    live.filter((p) => !p.session).map((p) => [p.person, p]),
  )
  const agents = new Map(
    live.filter((p) => p.session).map((p) => [p.signer, p]),
  )
  const devices = new Set(live.filter((p) => !p.session).map((p) => p.signer))
  const invited = invitedPeople(known.invites)
  const inviteKeys = pendingInviteKeys(known.invites)
  const bound = known.keys ?? { devices: {}, agents: {}, grantedBy: {} }
  const isSelf = (id: string) => known.self?.person === id
  /** A person's name: here now, then the viewer's own, then as invited. */
  const nameOf = (id: string) =>
    people.get(id)?.name ||
    (isSelf(id) ? `${known.self?.name || 'You'} (you)` : undefined) ||
    invited.get(id)
  const named = (id: string) => !!nameOf(id) && principals.has(id)
  // A device or key of a named person folds into that person's row, unless
  // it also has a grant of its own, which must stay visible.
  const folded = (id: string) => {
    const person = bound.devices[id]
    const row = principals.get(id)
    return (
      devices.has(id) ||
      (!!person && named(person) && !!row?.via.every((v) => v === person))
    )
  }
  const keyCount = new Map<string, number>()
  for (const [key, person] of Object.entries(bound.devices))
    if (folded(key) && !devices.has(key))
      keyCount.set(person, (keyCount.get(person) ?? 0) + 1)
  const role = (level: number) =>
    level === 3 ? 'Owner' : level === 2 ? 'Can edit' : 'Can view'
  return membership.principals
    .filter((p) => !folded(p.id))
    .map((p): MemberRow => {
      const person = people.get(p.id)
      const agent = agents.get(p.id)
      const agentPerson = agent?.person ?? bound.agents[p.id]
      const personName = nameOf(p.id)
      const via = p.via
        .map((id) =>
          id === 'direct'
            ? 'direct grant'
            : `via ${nameOf(id) || `group ${short(id)}`}`,
        )
        .join('; ')
      const extra: string[] = []
      if (p.kind === 'group' && invited.has(p.id))
        extra.push(`invited as ${invited.get(p.id)}`)
      const keys = keyCount.get(p.id)
      if (keys) extra.push(`${keys} ${keys === 1 ? 'device' : 'devices'}`)
      const owner = bound.devices[p.id]
      if (owner && !agentPerson)
        extra.push(`${nameOf(owner) || `person ${short(owner)}`}'s device`)
      const granted = (bound.grantedBy[p.id] ?? []).filter(
        (id) => id !== agentPerson && id !== owner,
      )
      if (granted.length)
        extra.push(
          `granted by ${granted.map((id) => nameOf(id) || short(id)).join(', ')}`,
        )
      const kind: MemberRow['kind'] =
        person || (p.kind === 'group' && personName)
          ? 'person'
          : agentPerson && (agent?.session || known.session)
            ? 'agent'
            : p.kind
      const ownerName = agentPerson
        ? nameOf(agentPerson) || `Person ${short(agentPerson)}`
        : undefined
      const name =
        kind === 'person'
          ? (personName as string)
          : kind === 'agent'
            ? agent?.name || `${ownerName}'s agent`
            : inviteKeys.has(p.id)
              ? `Invite for ${inviteKeys.get(p.id)}`
              : `${p.kind === 'group' ? 'Group' : 'Key'} ${short(p.id)}`
      return {
        id: p.id,
        kind,
        name,
        reason: [`${role(p.level)} · ${via}`, ...extra].join(' · '),
        present: !!person || !!agent,
        ...(kind === 'agent'
          ? {
              sessionId: (agent?.session || known.session) as string,
              personName: ownerName as string,
            }
          : {}),
      }
    })
    .sort(
      (a, b) =>
        (a.kind === 'person' ? 0 : a.kind === 'agent' ? 1 : 2) -
          (b.kind === 'person' ? 0 : b.kind === 'agent' ? 1 : 2) ||
        a.name.localeCompare(b.name),
    )
}
